
import { useEffect, useRef, useState } from "react";

export default function AudioWavePlayer({ src }) {
    const audioRef = useRef(null);
    const sliderRef = useRef(null);

    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [dragging, setDragging] = useState(false);


    const rafRef = useRef(null);


    const startProgressLoop = () => {
        const update = () => {
            if (audioRef.current && duration && !dragging) {
                setProgress(
                    (audioRef.current.currentTime / duration) * 100
                );
                rafRef.current = requestAnimationFrame(update);
            }
        };
        rafRef.current = requestAnimationFrame(update);
    };

    const stopProgressLoop = () => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };

    useEffect(() => {
        const handler = (e) => {
            if (audioRef.current && e.detail !== audioRef.current) {
                audioRef.current.pause();
                setPlaying(false);
            }
        };

        window.addEventListener("audio-play", handler);
        return () => window.removeEventListener("audio-play", handler);
    }, []);

    useEffect(() => {
        return () => stopProgressLoop();
    }, []);



    const togglePlay = () => {
        if (!audioRef.current) return;

        if (!playing) {
            // 🔔 Tell all other players to stop
            window.dispatchEvent(
                new CustomEvent("audio-play", { detail: audioRef.current })
            );
            audioRef.current.play();
            startProgressLoop();

        } else {
            audioRef.current.pause();
            stopProgressLoop();

        }

        setPlaying(!playing);
    };




    const seek = (clientX) => {
        if (!sliderRef.current || !audioRef.current) return;
        const rect = sliderRef.current.getBoundingClientRect();
        const percent = Math.min(
            Math.max((clientX - rect.left) / rect.width, 0),
            1
        );
        audioRef.current.currentTime = percent * duration;
        setProgress(percent * 100);
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div>
            <div className="d-flex align-items-center gap-2" style={{ width: 200 }}>
                {/* ▶️ Play */}
                <button
                    onClick={togglePlay}
                    className="btn p-0 d-flex align-items-center justify-content-center"
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "#bccde7ff",
                    }}
                >
                    <i className={`bi text-black ${playing ? "bi-pause-fill" : "bi-play-fill"}`} />
                </button>

                {/* 🌊 Slider */}
                <div
                    ref={sliderRef}
                    className="flex-grow-1 position-relative"
                    style={{ height: 26, cursor: "pointer" }}
                    onMouseDown={(e) => {
                        setDragging(true);
                        seek(e.clientX);
                    }}
                    onMouseMove={(e) => dragging && seek(e.clientX)}
                    onMouseUp={() => setDragging(false)}
                    onMouseLeave={() => setDragging(false)}
                >
                    {/* Base straight line (UNPLAYED PART ONLY) */}
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: `${progress}%`,
                            right: 0,
                            height: 2,
                            background: "#bdbdbd",
                            transform: "translateY(-50%)",
                        }}
                    />

                    {/* Played wave (CLIPPED) */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            height: "100%",
                            width: `${progress}%`,
                            overflow: "hidden",
                        }}
                    >
                        <svg
                            width="200%"
                            height="100%"
                            viewBox="0 0 600 26"
                            preserveAspectRatio="none"
                            style={{
                                animation: playing
                                    ? "waveMove 3.5s linear infinite"
                                    : "none",
                            }}
                        >
                            <path
                                d="
                    M0 13
                    C 10 8, 20 8, 30 13
                    C 40 18, 50 18, 60 13
                    C 70 8, 80 8, 90 13
                    C 100 18, 110 18, 120 13
                    C 130 8, 140 8, 150 13
                    C 160 18, 170 18, 180 13
                    C 190 8, 200 8, 210 13
                    C 220 18, 230 18, 240 13
                    C 250 8, 260 8, 270 13
                    C 280 18, 290 18, 300 13
                    C 310 8, 320 8, 330 13
                    C 340 18, 350 18, 360 13
                    C 370 8, 380 8, 390 13
                    C 400 18, 410 18, 420 13
                    C 430 8, 440 8, 450 13
                    C 460 18, 470 18, 480 13
                    C 490 8, 500 8, 510 13
                    C 520 18, 530 18, 540 13
                    C 550 8, 560 8, 570 13
                    C 580 18, 590 18, 600 13
                  "
                                fill="none"
                                stroke="#52991b"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>

                    {/* Handle */}
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: `${progress}%`,
                            width: 6,
                            height: 18,
                            background: "#52991b",
                            borderRadius: 4,
                            transform: "translate(-50%, -50%)",
                        }}
                    />
                </div>
            </div>

            {/* Time Display */}
            <div className="text-center" style={{ fontSize: "12px", color: "#bdbdbd" }}>
                {formatTime(audioRef.current?.currentTime || 0)} / {formatTime(duration)}
            </div>

            {/* Audio */}
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onLoadedMetadata={(e) => setDuration(e.target.duration)}
                onEnded={() => {
                    stopProgressLoop();
                    setPlaying(false);
                    setProgress(0);
                }}
            />

            {/* 🔑 Wave animation */}
            <style>
                {`
          @keyframes waveMove {
            from {
              transform: translateX(0);
            }
            to {
              transform: translateX(-50%);
            }
          }
        `}
            </style>
        </div>
    );
}
